<template>
  <div
    class="cg-chip-group"
    :class="{ 'cg-chip-group--column': column }"
  >
    <div
      v-show="showPrev"
      class="cg-chip-group__icon cg-chip-group__icon--left"
      role="button"
      @click="onScrollPrev()"
    >
      <div>
        <cg-icon-chevron />
      </div>
    </div>

    <div class="cg-chip-group__wrapper" ref="wrapper">
      <div
        class="cg-chip-group__content"
        :style="`transform: translateX(${-scrollOffset}px);`"
        ref="content"
      >
        <cg-chip
          v-for="(chipData, index) in chipsToDisplay"
          :key="index"
          bordered
          :small="smallChips"
          :class="{ 'cg-chip--active': isActive(chipData) }"
          tabindex="0"
          role="button"
          v-bind="getChipProps(chipData)"
          @click="clickHandler(chipData)"
          @keypress="clickHandler(chipData)"
        >
          {{ chipData.title }}
        </cg-chip>
      </div>
    </div>

    <div
      v-show="showNext"
      class="cg-chip-group__icon cg-chip-group__icon--right"
      role="button"
      @click="onScrollNext()"
    >
      <div>
        <cg-icon-chevron />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { Colors } from '../../utils/Colors';

import type {
  ChipGroupItem,
  ChipGroupItemStyles,
  ChipBadgeOptions,
  ChipGroupBadgeStyles,
} from '../../types';

import CgChip from '../CgChip';
// @ts-ignore
import { CgIconChevron } from '../../icons';

interface IChipProps extends ChipGroupItemStyles {
  badge?: ChipBadgeOptions;
}

export default Vue.extend({
  name: 'CgChipGroup',
  components: {
    CgChip,
    CgIconChevron,
  },
  props: {
    /**
     * Determines which chip is active 
     * @model
     */
    value: {
      // TODO array??
      type: [Number, String],
      required: false,
      default: null,
    },
    /**
     * The chips that will be displayed - can be an array of strings or an object
     */
    chipsData: {
      type: Array as PropType<Array<ChipGroupItem> | Array<string>>,
      required: true,
    },
    /**
     * Makes the chips small.
     */
    smallChips: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Removes arrows and wrap items if needed.
     */
    column: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Applies styles for chips.
     */
    chipStyles: {
      type: Object as PropType<ChipGroupItemStyles>,
      required: false,
      default: () => ({
        color: Colors.White,
        fontColor: Colors.Grey500,
        hoverColor: Colors.Teal100,
      }),
    },
    /**
     * Applies styles for badges.
     */
    badgeStyles: {
      type: Object as PropType<ChipGroupBadgeStyles>,
      required: false,
      default: () => ({
        color: Colors.Teal200,
        fontColor: Colors.Teal600,
      }),
    },
    /**
     * Applies styles for active chip.
     */
    activeChipStyles: {
      type: Object as PropType<ChipGroupItemStyles>,
      required: false,
      default: () => ({
        color: Colors.Teal600,
        fontColor: Colors.White,
        hoverColor: Colors.Teal600,
      }),
    },
  },
  data: () => ({
    lazyValue: null as any,
    isOverflowing: false,
    scrollOffset: 0,
    widths: {
      content: 0,
      wrapper: 0,
    },
  }),
  computed: {
    modelValue: {
      get(): string | number | null { return this.lazyValue; },
      set(val: string | number | null) {
        this.lazyValue = val;
        /**
         * Emitted when the v-model is changed.
         * 
         * @property {string | number | null} value new value
         */
        this.$emit('input', this.lazyValue);
      }
    },
    chipsToDisplay (): Array<ChipGroupItem> {
      const { chipsData } = this;

      if (!chipsData.length) { return [] }

      if (typeof chipsData[0] === 'string') {
        return (chipsData as Array<string>)
          ?.map<ChipGroupItem>((chipValue: string) => ({ key: chipValue, title: chipValue }));
      }

      return chipsData as Array<ChipGroupItem>;
    },
    showPrev (): boolean {
      return this.scrollOffset !== 0;
    },
    showNext (): boolean {
      const {
        content,
        wrapper
      } = this.widths;

      return content > Math.abs(this.scrollOffset) + wrapper;
    },
  },
  watch: {
    value (val: any) {
      this.lazyValue = val;
    },
    isOverflowing: 'setWidth',
    chipsToDisplay: {
      deep: true,
      handler: 'setWidth',
    },
  },
  mounted() {
    this.lazyValue = this.value;

    if (typeof ResizeObserver !== 'undefined') {
      const obs = new ResizeObserver(() => {
        this.setWidth();
      });
      obs.observe(this.$el);
      obs.observe(this.$refs.content as Element);
    } else {
      this.setWidth();
    }
  },
  methods: {
    isActive ({ key }: ChipGroupItem): boolean {
      // TODO handle array
      const { lazyValue } = this;

      return lazyValue === key;
    },
    getChipProps (chipData: ChipGroupItem): IChipProps {
      const isActive = this.isActive(chipData);
      const { badge } = chipData
      const { chipStyles, activeChipStyles, badgeStyles } = this;

      let result: IChipProps = { ...chipStyles };

      if (isActive) {
        result = { ...activeChipStyles };
      }

      if (badge) {
        result.badge = {
          content: badge,
          ...badgeStyles,
        };
      }

      return result;
    },
    clickHandler ({ key }: ChipGroupItem): void {
      this.modelValue = key;
    },
    setWidth (): void {
      const {
        content,
        wrapper,
      } = this.$refs;

      this.widths = {
        content: content ? (content as Element).clientWidth : 0,
        wrapper: wrapper ? (wrapper as Element).clientWidth : 0,
      };

      this.isOverflowing = this.widths.wrapper + 1 < this.widths.content;
      this.scrollOffset = 0;
    },
    onScrollPrev (): void {
      const {
        widths: { wrapper },
        scrollOffset
      } = this

      if (scrollOffset !== 0) {
        // 2 * 44 - arrow size is 44
        const newScrollOffset = scrollOffset - wrapper + (2 * 44);
        this.scrollOffset = Math.max(0, newScrollOffset - 44 < 0 ? 0 : newScrollOffset);
      }
    },
    onScrollNext (): void {
      const {
        widths: {
          wrapper,
          content,
        },
        scrollOffset
      } = this

      if (scrollOffset !== content) {
        // 2 * 44 - arrow size is 44
        const newScrollOffset = scrollOffset + wrapper - (2 * 44);
        const maxScrollOffset = content - wrapper;
        const isItNearTheEnd = newScrollOffset + 44 > (maxScrollOffset);
        this.scrollOffset = Math.min(maxScrollOffset, isItNearTheEnd ? maxScrollOffset : newScrollOffset);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
